import React, { useState } from 'react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const REVENUE_DATA = [
  { month:'Jan', gross:1200,  net:840,   deals:3  },
  { month:'Feb', gross:2800,  net:1960,  deals:7  },
  { month:'Mar', gross:4050,  net:2835,  deals:11 },
  { month:'Apr', gross:8100,  net:5670,  deals:18 },
  { month:'May', gross:12000, net:8400,  deals:24 },
  { month:'Jun', gross:16200, net:11340, deals:31 },
];

const DEAL_STAGE_DATA = [
  { stage:'Lead',        count:12, value:0    },
  { stage:'Outreach',    count:18, value:3150 },
  { stage:'Engaged',     count:9,  value:2625 },
  { stage:'Onboarding',  count:5,  value:3500 },
  { stage:'Activated',   count:27, value:38500},
];

const EMAIL_PERF = [
  { name:'Club Onboarding Blast', sent:3527, opens:2398, clicks:1194 },
  { name:'Scout Activation',      sent:1632, opens:1159, clicks:686  },
  { name:'Premium Upsell',        sent:94,   opens:67,   clicks:31   },
];

const SCOUT_PERF = [
  { name:'Emma Thompson',  evals:22, earnings:385 },
  { name:'Tom Nakamura',   evals:18, earnings:315 },
  { name:'Alex Aldridge',  evals:14, earnings:244 },
  { name:'Henrik Larsson', evals:11, earnings:193 },
  { name:'Carlos Mendez',  evals:7,  earnings:123 },
];

const CONTACT_SOURCES = [
  { name:'Verified List', value:3200, color:'#A7F432' },
  { name:'Paid Social',   value:890,  color:'#4fc3f7' },
  { name:'Referral',      value:620,  color:'#f5a623' },
  { name:'Organic',       value:310,  color:'#a78bfa' },
  { name:'Influencer',    value:139,  color:'#8a9a97' },
];

const Tip = ({ active, payload, label }) => active && payload?.length ? (
  <div style={{ background:'#131B1B', border:'1px solid #2a3a3a', borderRadius:8, padding:'10px 14px', fontSize:12 }}>
    <p style={{ color:'var(--text-muted)', marginBottom:4 }}>{label}</p>
    {payload.map((p,i) => <p key={i} style={{ color:p.color }}>{p.name}: {typeof p.value === 'number' && p.name?.includes('$') ? '$' : ''}{p.value?.toLocaleString()}</p>)}
  </div>
) : null;

export default function Reports() {
  const [range, setRange] = useState('6m');

  return (
    <div>
      <div className="stats-grid">
        <div className="stat-card"><div className="stat-label">Total revenue</div><div className="stat-value">$4,050</div><div className="stat-sub stat-up">↑ 44% vs last month</div></div>
        <div className="stat-card"><div className="stat-label">Deals closed</div><div className="stat-value">27</div><div className="stat-sub stat-up">↑ 8 this week</div></div>
        <div className="stat-card"><div className="stat-label">Avg deal size</div><div className="stat-value">$437</div><div className="stat-sub">Per activated club</div></div>
        <div className="stat-card"><div className="stat-label">Email open rate</div><div className="stat-value">58%</div><div className="stat-sub stat-up">Above benchmark</div></div>
      </div>

      {/* Range toggle */}
      <div style={{ display:'flex', gap:6, marginBottom:20 }}>
        {['1m','3m','6m','1y'].map(r => (
          <button key={r} className={`pill ${range===r?'active':''}`} onClick={() => setRange(r)}>{r}</button>
        ))}
        <button className="btn btn-ghost" style={{ marginLeft:'auto', fontSize:12 }}>Export PDF</button>
      </div>

      {/* Revenue + deals */}
      <div className="two-col" style={{ marginBottom:20 }}>
        <div className="card">
          <div className="card-header"><span className="card-title">Revenue over time</span><span className="badge badge-green">Gross vs Net</span></div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={REVENUE_DATA} margin={{ top:4, right:4, left:-20, bottom:0 }}>
              <defs>
                <linearGradient id="rg1" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#A7F432" stopOpacity={0.2}/><stop offset="95%" stopColor="#A7F432" stopOpacity={0}/></linearGradient>
                <linearGradient id="rg2" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#4fc3f7" stopOpacity={0.2}/><stop offset="95%" stopColor="#4fc3f7" stopOpacity={0}/></linearGradient>
              </defs>
              <XAxis dataKey="month" tick={{ fill:'#646B6B', fontSize:11 }} axisLine={false} tickLine={false}/>
              <YAxis tick={{ fill:'#646B6B', fontSize:11 }} axisLine={false} tickLine={false} tickFormatter={v=>`$${(v/1000).toFixed(0)}k`}/>
              <Tooltip content={<Tip />}/>
              <Area type="monotone" dataKey="gross" name="Gross" stroke="#A7F432" strokeWidth={2} fill="url(#rg1)"/>
              <Area type="monotone" dataKey="net"   name="Net"   stroke="#4fc3f7" strokeWidth={2} fill="url(#rg2)"/>
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <div className="card-header"><span className="card-title">Deals by stage</span></div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={DEAL_STAGE_DATA} margin={{ top:4, right:4, left:-20, bottom:0 }}>
              <XAxis dataKey="stage" tick={{ fill:'#646B6B', fontSize:10 }} axisLine={false} tickLine={false}/>
              <YAxis tick={{ fill:'#646B6B', fontSize:11 }} axisLine={false} tickLine={false}/>
              <Tooltip content={<Tip />}/>
              <Bar dataKey="count" name="Deals" fill="#A7F432" radius={[4,4,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Email + contacts */}
      <div className="two-col" style={{ marginBottom:20 }}>
        <div className="card">
          <div className="card-header"><span className="card-title">Email campaign performance</span></div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Campaign</th><th>Sent</th><th>Opens</th><th>Clicks</th><th>Open %</th></tr></thead>
              <tbody>
                {EMAIL_PERF.map((e,i) => (
                  <tr key={i}>
                    <td style={{ fontWeight:500, fontSize:12 }}>{e.name}</td>
                    <td>{e.sent.toLocaleString()}</td>
                    <td style={{ color:'var(--accent)' }}>{e.opens.toLocaleString()}</td>
                    <td style={{ color:'#4fc3f7' }}>{e.clicks.toLocaleString()}</td>
                    <td style={{ fontWeight:600 }}>{Math.round((e.opens/e.sent)*100)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><span className="card-title">Contact sources</span></div>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={CONTACT_SOURCES} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                {CONTACT_SOURCES.map((e,i) => <Cell key={i} fill={e.color}/>)}
              </Pie>
              <Legend iconType="circle" iconSize={8} formatter={v => <span style={{ color:'#8a9a97', fontSize:11 }}>{v}</span>}/>
              <Tooltip contentStyle={{ background:'#131B1B', border:'1px solid #2a3a3a', borderRadius:8, color:'#e5eee5', fontSize:12 }}/>
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Scout leaderboard */}
      <div className="card">
        <div className="card-header"><span className="card-title">Scout leaderboard</span><span className="badge badge-amber">Top performers</span></div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Rank</th><th>Scout</th><th>Evaluations</th><th>Earnings</th><th>Performance</th></tr></thead>
            <tbody>
              {SCOUT_PERF.map((s,i) => (
                <tr key={i}>
                  <td style={{ fontWeight:700, color: i===0?'#A7F432':i===1?'#4fc3f7':i===2?'#f5a623':'var(--text-dim)' }}>#{i+1}</td>
                  <td style={{ fontWeight:500 }}>{s.name}</td>
                  <td><strong>{s.evals}</strong> evals</td>
                  <td style={{ color:'#A7F432', fontWeight:600 }}>${s.earnings}</td>
                  <td>
                    <div style={{ width:120, height:6, background:'var(--border)', borderRadius:3, overflow:'hidden' }}>
                      <div style={{ width:`${(s.evals/22)*100}%`, height:'100%', background: i===0?'#A7F432':'#4fc3f7', borderRadius:3 }}/>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
